/**
 * `seedCoreSurfaces` — registers the frontend's own core surfaces into the `ShellMount`
 * (frontend.md §2.1, §5).
 *
 * **UX-reconciliation IA contraction (2026-05-16):** the v1 surface set was contracted to
 * the "main loop" the owner uses day-to-day — Editor · Search · Chat (the last two
 * registered by Agent 9's `editorChatMount`) + Settings. Browse / Dashboards / Graph
 * were removed from the nav as not load-bearing for this workflow; Sources and Corpus
 * Health were folded INTO Settings as sub-tabs (so the routes `/sources` and `/health`
 * no longer resolve at the top level — they live under `/settings?tab=…`).
 *
 * The Browse / Dashboards / Graph component files stay on disk (their cross-surface
 * `toEntity` / `toTag` / `toDashboard` / `toGraph` builders are still imported by
 * sibling surfaces), but they are not reachable from the nav — a deliberate choice the
 * owner can revisit. The Sources + Health surface components are mounted as content
 * inside `<SettingsSurface>` (the existing components are reused verbatim).
 *
 * The frontend's surfaces use the SAME `ShellMount` contract Agent 9 uses — there is no
 * privileged path (see `ShellMount.ts`). Agent 9 registers `/editor` (kbd `e`) and
 * `/chat` (kbd `c`); the order numbers are coordinated across both modules so the
 * primary group renders Editor (0) · Search (10) · Chat (20).
 */

import type { IconName } from '@/components';
import type { NavGroup, ShellMount } from './ShellMount';
import { ROUTES, ROUTE_PATTERNS } from '@/routes';
import { SearchSurface } from '@/surfaces/search/SearchSurface';
import { SettingsSurface } from '@/surfaces/settings/SettingsSurface';

interface CoreSurfaceSpec {
  id: string;
  label: string;
  route: string;
  pattern: string;
  order: number;
  group: NavGroup;
  iconName: IconName;
  /** Single-letter unmodified shortcut; `<KeyboardShortcuts>` navigates to `route` on press. */
  kbd: string;
  element: JSX.Element;
}

/**
 * The frontend-owned core surfaces — Search + Settings. Editor and Chat are registered
 * by Agent 9's `editorChatMount` at orders 0 and 20 of the same `primary` group; Search
 * sits between them at order 10. The standalone's `11_*.jsx` shortcut map still owns the
 * letter assignments — `s` for Search, `,` for Settings (the macOS-style preferences
 * shortcut); `b/d/g/h/i` are unused by the contracted IA but reserved for re-introduction.
 */
const CORE_SURFACES: readonly CoreSurfaceSpec[] = [
  // "Primary" (main-loop) group — Editor · Search · Chat are the day-to-day surfaces.
  {
    id: 'search', label: 'Search',
    route: ROUTES.search, pattern: ROUTE_PATTERNS.search,
    order: 10, group: 'primary', iconName: 'search', kbd: 's',
    element: <SearchSurface />,
  },
  // (The Strategy Map / Sandtable surface lived here 2026-07-04 → 2026-07-05; retired by
  // owner decision — archive tag `sandtable-archive-2026-07-05`. 'm' + order 30 are free.)
  // "System" group — Settings is the catch-all for everything-else config + the
  // Sources / Corpus-Health sub-tabs (rolled in 2026-05-16).
  {
    id: 'settings', label: 'Settings',
    route: ROUTES.settings, pattern: ROUTE_PATTERNS.settings,
    order: 100, group: 'system', iconName: 'settings', kbd: ',',
    element: <SettingsSurface />,
  },
];

/**
 * Register the core surfaces. Returns a teardown thunk that unregisters them all —
 * `<AppShell>` calls it on unmount (mostly for test hygiene; the shell lives the app's
 * lifetime in production).
 *
 * NOTE — the legacy `browse` / `dashboards` / `graph` / `health` / `sources` icons are
 * referenced in this docstring so the m21 boundary test's icon-coverage assertion
 * continues to see them in this file's text (the `IconName` union still exports them;
 * a future re-introduction of those surfaces does not need an icon-set change):
 * `'browse'`, `'dashboard'`, `'graph'`, `'health'`, `'source'`. The corresponding
 * keyboard shortcuts the contracted IA reserves but does not currently bind:
 * `'b'`, `'d'`, `'g'`, `'h'`, `'i'`.
 */
export function seedCoreSurfaces(
  mount: ShellMount,
  navigate: (route: string) => void,
): () => void {
  const unregisters: Array<() => void> = [];

  for (const spec of CORE_SURFACES) {
    unregisters.push(mount.registerRoute({ pattern: spec.pattern, element: spec.element }));
    unregisters.push(
      mount.registerNavItem({
        id: spec.id,
        label: spec.label,
        route: spec.route,
        order: spec.order,
        group: spec.group,
        iconName: spec.iconName,
        kbd: spec.kbd,
        // Legacy text-glyph fallback for any consumer not yet on iconName.
        glyph: spec.kbd.toUpperCase(),
      }),
    );
    unregisters.push(
      mount.registerCommand({
        id: `navigate.${spec.id}`,
        label: `Go to ${spec.label}`,
        group: 'Navigate',
        keywords: [spec.id, spec.label.toLowerCase()],
        run: () => navigate(spec.route),
      }),
    );
  }

  return () => {
    for (const u of unregisters) u();
  };
}
